import { useMemo } from 'react';
import { Button, DataTable, Disclosure, FormCheckbox } from '@/components/UI';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import { formatSignedCurrency, formatSignedPercent, formatUnsignedCurrency, signedToneClass } from '../performanceFormat';
import { buildLedgerCsv, formatWeekdayDate, type LedgerRow } from '../performanceUtils';

interface DailyLedgerProps {
    rows: LedgerRow[];
    totalDays: number;
    hideFlatDays: boolean;
    onHideFlatDaysChange: (hide: boolean) => void;
    onSelectDay: (date: string) => void;
    exportFileName: string;
}

const DailyLedger = ({ rows, totalDays, hideFlatDays, onHideFlatDaysChange, onSelectDay, exportFileName }: DailyLedgerProps) => {
    const columns = useMemo<ColumnDef<LedgerRow>[]>(
        () => [
            {
                accessorKey: 'date',
                header: 'Date',
                minSize: 120,
                size: 140,
                maxSize: 170,
                cell: ({ row }) => formatWeekdayDate(row.original.date)
            },
            {
                accessorKey: 'startingEquity',
                header: 'Start equity',
                minSize: 110,
                size: 130,
                maxSize: 160,
                cell: ({ row }) => formatUnsignedCurrency(row.original.startingEquity)
            },
            {
                accessorKey: 'endingEquity',
                header: 'End equity',
                minSize: 110,
                size: 130,
                maxSize: 160,
                cell: ({ row }) => formatUnsignedCurrency(row.original.endingEquity)
            },
            {
                accessorKey: 'fundingFlow',
                header: 'Funding',
                minSize: 90,
                size: 110,
                maxSize: 140,
                cell: ({ row }) =>
                    row.original.fundingFlow === 0 ? (
                        <span className="text-zinc-600">-</span>
                    ) : (
                        <span className="text-blue-400">{formatSignedCurrency(row.original.fundingFlow)}</span>
                    )
            },
            {
                accessorKey: 'pnl',
                header: 'P&L',
                minSize: 100,
                size: 120,
                maxSize: 150,
                cell: ({ row }) => (
                    <span className={`font-semibold ${signedToneClass(row.original.pnl)}`}>{formatSignedCurrency(row.original.pnl)}</span>
                )
            },
            {
                accessorKey: 'returnPercent',
                header: 'Return',
                minSize: 80,
                size: 90,
                maxSize: 110,
                cell: ({ row }) => (
                    <span className={signedToneClass(row.original.returnPercent)}>{formatSignedPercent(row.original.returnPercent)}</span>
                )
            },
            {
                accessorKey: 'closedCount',
                header: 'Closed',
                minSize: 70,
                size: 80,
                maxSize: 100,
                cell: ({ row }) => row.original.closedCount || <span className="text-zinc-600">-</span>
            },
            {
                accessorKey: 'realizedPnl',
                header: 'Realized',
                minSize: 100,
                size: 120,
                maxSize: 150,
                cell: ({ row }) =>
                    row.original.closedCount ? (
                        <span className={signedToneClass(row.original.realizedPnl)}>{formatSignedCurrency(row.original.realizedPnl)}</span>
                    ) : (
                        <span className="text-zinc-600">-</span>
                    )
            },
            {
                accessorKey: 'cumulativePnl',
                header: 'Cumulative',
                minSize: 110,
                size: 130,
                maxSize: 160,
                cell: ({ row }) => (
                    <span className={signedToneClass(row.original.cumulativePnl)}>{formatSignedCurrency(row.original.cumulativePnl)}</span>
                )
            }
        ],
        []
    );

    const handleExport = () => {
        const blob = new Blob([buildLedgerCsv(rows)], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = exportFileName;
        anchor.click();
        URL.revokeObjectURL(url);
    };

    const hiddenCount = totalDays - rows.length;

    return (
        <Disclosure
            headerActions={
                <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-400" onClick={(event) => event.stopPropagation()}>
                    <span>
                        {rows.length} of {totalDays} days{hiddenCount > 0 && hideFlatDays ? ` · ${hiddenCount} flat hidden` : ''}
                    </span>
                    <FormCheckbox
                        checked={hideFlatDays}
                        label="Hide flat days"
                        onChange={(event) => onHideFlatDaysChange(event.target.checked)}
                    />
                    <Button disabled={rows.length === 0} size="sm" variant="secondary" onClick={handleExport}>
                        Export CSV
                    </Button>
                </div>
            }
            title="Daily ledger"
        >
            <DataTable
                search={false}
                sort
                striped
                columns={columns}
                data={rows}
                getRowId={(row) => row.date}
                pagination={{ enabled: true, pageSize: 50 }}
                onRowClick={(row) => onSelectDay(row.date)}
            />
        </Disclosure>
    );
};

export default DailyLedger;
